// Zweck: OpsApp-Routen mit angemeldeter Lagerfunktion und getrenntem Testeinstieg. Stand: 21.10.2026.
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QueueProvider } from './contexts/QueueContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { ONE360_COLDSTART_HINT_HOSTNAME } from './constants/auth';
import { useDocumentStore } from './store/documentState';
import type { DocumentUploadQueueData, QueueItemsPermanentlyFailedDetail } from './interfaces/queue';
import AuthGuard from './components/AuthGuard';
import BottomNavigation from './components/BottomNavigation';
import SyncStatus from './components/SyncStatus';
import LoginScreen from './pages/LoginScreen';
import ProjectsScreen from './pages/ProjectsScreen';
import PhasePickerScreen from './pages/PhasePickerScreen';
import PhaseScreen from './pages/PhaseScreen';
import SyncScreen from './pages/SyncScreen';
import HubSpotDealsPage from './pages/HubSpotDealsPage';
import WarehouseScreen from './pages/WarehouseScreen';
import WarehouseDevicesPage from './pages/WarehouseDevicesPage';
import { applyTestIDs } from './utils/testUtils';
import './assets/index.css';

const WarehouseDevicesTest = (import.meta.env.DEV || import.meta.env.MODE === 'warehouse-test')
  ? lazy(() => import('./pages/WarehouseDevicesTest')) : null;

function App() {
  // Test-IDs für E2E-Tests anwenden
  useEffect(() => {
    // Beim ersten Rendern und dann alle 2 Sekunden (für dynamisch hinzugefügte Elemente)
    applyTestIDs();
    const interval = setInterval(applyTestIDs, 2000);
    return () => clearInterval(interval);
  }, []);

  // Die isolierte Simulation startet keine echten Auth- oder Upload-Dienste.
  if (WarehouseDevicesTest && window.location.pathname === '/lager/geraete-test') {
    return <ThemeProvider><Suspense fallback={<p>Gerätetest wird geladen …</p>}><WarehouseDevicesTest /></Suspense></ThemeProvider>;
  }

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <QueueProvider>
            <ToastProvider>
              <AppLayout />
            </ToastProvider>
          </QueueProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Layout-Komponente mit Bottom-Navigation
const AppLayout: React.FC = () => {
  const location = useLocation();
  const appVersion = __APP_BUILD_ID__;
  const { isSlowLoginRetry } = useAuth();
  const { showToast } = useToast();

  // Bottom-Navigation nur auf geschützten Screens anzeigen (nicht auf Login).
  // Auf einem aktiven Phasen-Screen ausblenden, weil die PhaseFooterNav (Issue #16)
  // dort die Navigation übernimmt und die globale BottomNav sonst doppelt Platz frisst.
  const isPhaseScreen = /^\/projects\/[^/]+\/phase\/[^/]+/.test(location.pathname);
  const isWarehouseScreen = /^\/lager(?:\/|$)/.test(location.pathname);
  const showBottomNav = location.pathname !== '/login' && !isPhaseScreen && !isWarehouseScreen;

  // Hinweis auf den One360-Cold-Start (siehe AUTH_ME_RETRY_TIMEOUT_MS) nur in der
  // Umgebung einblenden, in der die One360-API tatsächlich auf 0 Instanzen
  // herunterskaliert (DEV) - in PROD (immer warm) wäre die Meldung irreführend,
  // ein Retry dort deutet auf einen echten Fehler statt auf einen Cold Start hin.
  useEffect(() => {
    const isColdStartHintEnvironment =
      window.location.hostname === ONE360_COLDSTART_HINT_HOSTNAME || window.location.hostname === 'localhost';
    if (isSlowLoginRetry && isColdStartHintEnvironment) {
      showToast('info', 'Anmeldung dauert länger als gewohnt – der Server wird gerade hochgefahren, bitte warten.', 20000);
    }
  }, [isSlowLoginRetry, showToast]);

  // ADO #1231 (Kriterium 4): Baustellenfotos werden über PhaseScreen.handleImageCapture per
  // `document_upload` eingereiht. Der dortige `.then/.catch` an addToQueue sieht nur das
  // Ergebnis eines SOFORTIGEN Verarbeitungsversuchs (online, direkt beim Enqueue) - ein OFFLINE
  // eingereihtes Foto, dessen Sync erst viel später (nächster Reconnect, siehe
  // QueueContext.handleOnlineStatus) endgültig scheitert, lief bisher komplett ohne
  // Benachrichtigung durch: das Dokument blieb optimistisch auf "completed" stehen, obwohl das
  // Foto nie ankam. Analog zum bestehenden Pattern in AbschlussberichtWizard: hier zentral statt
  // pro Phase-Screen-Instanz, da PhaseScreen für die betroffene Phase zum Zeitpunkt eines
  // verzögerten Sync-Fehlschlags oft längst wieder unmounted ist. useDocumentStore ist ein
  // Zustand-Store (kein Context) und daher auch außerhalb von PhaseScreen ansprechbar.
  useEffect(() => {
    const handlePermanentFailure = (event: Event) => {
      const detail = (event as CustomEvent<QueueItemsPermanentlyFailedDetail>).detail;
      const failedDocumentUploads = detail?.permanentlyFailedItems.filter((item) => item.type === 'document_upload');
      if (!failedDocumentUploads || failedDocumentUploads.length === 0) {
        return;
      }

      const { updateDocumentStatus } = useDocumentStore.getState();
      failedDocumentUploads.forEach((item) => {
        const { documentId, phaseId } = (item.data || {}) as Partial<DocumentUploadQueueData>;
        if (documentId && phaseId) {
          updateDocumentStatus(phaseId, documentId, 'pending');
        }
      });

      showToast(
        'error',
        failedDocumentUploads.length === 1
          ? 'Ein Foto-Upload ist endgültig fehlgeschlagen und wurde zurückgesetzt.'
          : `${failedDocumentUploads.length} Foto-Uploads sind endgültig fehlgeschlagen und wurden zurückgesetzt.`,
        6000,
      );
    };

    window.addEventListener('queue-item-permanently-failed', handlePermanentFailure);
    return () => window.removeEventListener('queue-item-permanently-failed', handlePermanentFailure);
  }, [showToast]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {showBottomNav && (
        <div className="pointer-events-none fixed right-2 top-2 z-50 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white">
          v:{appVersion}
        </div>
      )}

      <Routes>
        {/* Lagerseiten prüfen Anmeldung selbst, damit gespeicherte Entwürfe offline erreichbar bleiben. */}
        <Route path="/lager" element={<WarehouseScreen />} />
        <Route path="/lager/geraete" element={<WarehouseDevicesPage />} />
        {(import.meta.env.DEV || import.meta.env.MODE === 'warehouse-test') && <Route path="/lager/test" element={<WarehouseScreen demo />} />}
        {/* Öffentliche Route */}
        <Route path="/login" element={<LoginScreen />} />
        
        {/* Geschützte Routen */}
        <Route path="/projects" element={
          <AuthGuard>
            <ProjectsScreen />
          </AuthGuard>
        } />
        
        {/* PhasePicker-Route */}
        <Route path="/projects/:id" element={
          <AuthGuard>
            <PhasePickerScreen />
          </AuthGuard>
        } />
        
        <Route path="/projects/:projectId/phase/:phaseId" element={
          <AuthGuard>
            <PhaseScreen />
          </AuthGuard>
        } />
        
        {/* Sync-Screen */}
        <Route path="/sync" element={
          <AuthGuard>
            <SyncScreen />
          </AuthGuard>
        } />
        
        {/* HubSpot Deals Screen */}
        <Route path="/hubspot-deals" element={
          <AuthGuard>
            <HubSpotDealsPage />
          </AuthGuard>
        } />

        {/* Fallback-Route */}
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
      
      {/* Bottom Navigation - nur auf geschützten Screens */}
      {showBottomNav && <BottomNavigation />}
      
      {/* Sync Status Bar - wird von SyncStatus selbst gesteuert */}
      {showBottomNav && <SyncStatus />}
    </div>
  );
};

export default App;
